import { makeStyles, Theme, Typography } from '@material-ui/core';
import React, { useEffect } from 'react';
import { CustomButtons } from '../common/footer-buttons/ButtonsComp';
import { LocationInformation } from '../location-information-comp/LocationInformation';
import { usePremiseSearchQuery } from '../../../../hooks/usePremiseSearchQuery';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import AccountStatusIcon from '../../../../static/Account_Status.svg';
import DimFlickeringIcon from '../../../../static/Dim_or_flickering_icon.svg';
import FullPartialIcon from '../../../../static/Full_or_partial_icon.svg';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { ResettingBreakersDialog } from '../resetting-breakers/ResettingBreakersDialog';
import DimOrFlickeringComp from '../dim-or-flickering/DimOrFlickeringComp';
import { useIsMobile } from '../../../../util/style-utils';
import useAuth from '../../../../hooks/useAuth';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    backgroundColor: colors.white,
    padding: '1rem 1.5rem',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
  },
  activeOutageTitle: {
    fontSize: 18,
  },
  flexBetween: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between',
    padding: '0 1.5rem',
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  mr2: {
    marginRight: theme.spacing(2),
  },
  ml1: {
    marginLeft: theme.spacing(1),
  },
  textLgMedium: {
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  cardRow: {
    display: 'flex',
    gap: '10px',
    paddingRight: '1.5rem',
    paddingLeft: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  card: {
    width: '50%',
    cursor: 'pointer',
    backgroundColor: colors.white,
    padding: '0.5rem 1rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  itemsCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textBase: {
    fontSize: '1rem',
  },
}));

interface TypeProps {
  outageExperienceType: string | null;
  setOutageExperienceType: (outageExperienceType: string) => void;
}

export const OutageTypeExperienceDetails = ({
  outageExperienceType,
  setOutageExperienceType,
}: TypeProps) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const {
    setReportOutageOpen,
    selectedAccountForOutage,
    outageMarkerView,
  } = useOutageLeftPanelState();
  const pid = selectedAccountForOutage?.premiseInfos?.[0]?.encryptedPremiseId;
  const accountNumber = selectedAccountForOutage?.accountNumber;
  const premiseInfo = selectedAccountForOutage?.premiseInfos?.[0];
  const { isAuthenticated } = useAuth();
  const hasKnownOutage = selectedAccountForOutage?.hasKnownOutage;

  const { setPremiseIds, data: premiseInfoData } = usePremiseSearchQuery();
  useEffect(() => {
    if (pid) {
      setPremiseIds([pid]);
    } else {
      setPremiseIds(undefined);
    }
  }, [pid, setPremiseIds]);
  const meterNumber =
    premiseInfoData?.premiseSearch?.[0]?.spList?.[0]?.meterNumber;

  const renderCardContent = (
    icon: React.ReactNode,
    header: React.ReactNode,
    content: React.ReactNode,
    cardType: string,
  ) => {
    return (
      <div
        className={classes.card}
        onClick={() => setOutageExperienceType(cardType)}
      >
        <div className={classes.itemsCenter}>
          <div className={classes.flex}>
            {icon}
            <p className={classes.ml1 + ' ' + classes.sectionTitle}>{header}</p>
          </div>
          <ChevronRightIcon />
        </div>
        <p className={classes.textBase}>{content}</p>
      </div>
    );
  };

  return (
    <>
      {!outageExperienceType && (
        <>
          <div className={classes.container}>
            <Typography
              className={
                hasKnownOutage && outageMarkerView !== OutageMarkerView.NoOutage
                  ? classes.activeOutageTitle
                  : classes.sectionTitle
              }
            >
              {hasKnownOutage && outageMarkerView !== OutageMarkerView.NoOutage
                ? richT('HAS_ACTIVE_OUTAGE')
                : t('WHAT_TYPE_OF_OUTAGE_ARE_YOU_EXPERIENCING_NEW')}
            </Typography>
            <LocationInformation
              accountNumber={accountNumber || ''}
              nickName={selectedAccountForOutage?.description || ''}
              addressLine1={premiseInfo?.addressLine1 || ''}
              city={premiseInfo?.city || ''}
              state={premiseInfo?.state || ''}
              postal={premiseInfo?.postal || ''}
            />
          </div>
          {isAuthenticated &&
            outageMarkerView !== OutageMarkerView.NoOutage &&
            !hasKnownOutage && (
              <div className={classes.flexBetween}>
                <Typography className={classes.textLgMedium}>
                  {richT('METER_NUMBER', {
                    METER_NUMBER: meterNumber || undefined,
                  })}
                </Typography>
                <div className={classes.flex}>
                  <Typography
                    className={classes.mr2 + ' ' + classes.textLgMedium}
                  >
                    {t('OUTAGE_POWER_ON')}
                  </Typography>
                  <AccountStatusIcon />
                </div>
              </div>
            )}
          {(!hasKnownOutage ||
            outageMarkerView === OutageMarkerView.NoOutage) && (
            <div
              className={classes.cardRow}
              style={{
                paddingTop:
                  isAuthenticated &&
                  outageMarkerView !== OutageMarkerView.NoOutage
                    ? ''
                    : '1.5rem',
              }}
            >
              {renderCardContent(
                <FullPartialIcon />,
                t('FULL_OR_PARTIAL_OUTAGE_V2'),
                t('FULL_OR_PARTIAL_CONTENT'),
                'fullPartial',
              )}
              {renderCardContent(
                <DimFlickeringIcon />,
                t('DIM_OR_FLICKERING_LIGHTS_V2'),
                t('DIM_OR_FLICKERING_CONTENT'),
                'dimFlickering',
              )}
            </div>
          )}
          <CustomButtons
            buttonOne={t('CANCEL')}
            buttonOneOnClick={() => {
              setReportOutageOpen(false);
            }}
          />
        </>
      )}

      {outageExperienceType === 'fullPartial' && <ResettingBreakersDialog />}
      {outageExperienceType === 'dimFlickering' && (
        <div style={{ margin: isMobile ? '20px' : '0 20px 20px' }}>
          <DimOrFlickeringComp orangeButton={true} />
        </div>
      )}
    </>
  );
};
